"use client";

/**
 * List ⇄ vinyl drawer (Gordon 2026-11-02: "like pulling out a drawer full of
 * vinyls — on the search page too, on the playlist page"). One switch, the
 * choice remembered per page on this device.
 */

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Disc3, LayoutList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * `remember: false` — always open as a list; the drawer is a choice for this
 * visit only (Gordon 2026-11-03: Search must not open in the crate view).
 */
export function useCrateMode(pageKey: string, { remember = true }: { remember?: boolean } = {}): [boolean, (on: boolean) => void] {
  const storageKey = `bb-crate-view:${pageKey}`;
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!remember) { try { localStorage.removeItem(storageKey); } catch { /* ignore */ } return; }
    try { setOn(localStorage.getItem(storageKey) === "1"); } catch { /* ignore */ }
  }, [storageKey, remember]);
  const set = (v: boolean) => { setOn(v); if (remember) { try { localStorage.setItem(storageKey, v ? "1" : "0"); } catch { /* ignore */ } } };
  return [on, set];
}

export function CrateToggle({ crate, onChange, className }: { crate: boolean; onChange: (on: boolean) => void; className?: string }) {
  const t = useTranslations("crates");
  return (
    <div role="group" aria-label={t("viewLabel")} className={cn("flex h-9 shrink-0 overflow-hidden rounded-md border", className)}>
      <Button type="button" variant={!crate ? "default" : "ghost"} size="icon" className="h-full w-9 rounded-none border-0" aria-pressed={!crate} aria-label={t("viewList")} title={t("viewList")} onClick={() => onChange(false)}>
        <LayoutList className="h-4 w-4" />
      </Button>
      <Button type="button" variant={crate ? "default" : "ghost"} size="icon" className="h-full w-9 rounded-none border-0 border-l" aria-pressed={crate} aria-label={t("viewCrate")} title={t("viewCrate")} onClick={() => onChange(true)}>
        <Disc3 className="h-4 w-4" />
      </Button>
    </div>
  );
}
